import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Mail, Send, CheckCircle, MessagesSquare } from 'lucide-react';
import MessengerSetupForm from './channels/MessengerSetupForm';
import EmailSettings from './EmailSettings';
import { httpClient } from '../../services/httpClient';
import FacebookPageConnector from '../omnichannel/FacebookPageConnector';
import LiveChatSetupForm from './channels/LiveChatSetupForm';

type ChannelType = 'messenger' | 'email' | 'viber' | 'live_chat' | null;

interface ChannelConnectionStatus {
  messenger: boolean;
  email: boolean;
  viber: boolean;
  live_chat: boolean;
}

export default function OmniChannelSettings() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [selectedChannel, setSelectedChannel] = useState<ChannelType>(null);
  const [connectionStatus, setConnectionStatus] = useState<ChannelConnectionStatus>({
    messenger: false,
    email: false,
    viber: false,
    live_chat: false
  });

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      // Check Messenger status
      const messengerStatus = await httpClient.get<{ logged_in: boolean }>(
        '/api/channels/messenger/login-status'
      );
      setConnectionStatus((prev) => ({
        ...prev,
        messenger: messengerStatus.logged_in || false
      }));

      // Check Email status - email is always configured if config exists
      try {
        const emailConfig = await httpClient.get<{ success: boolean; data: any }>(
          '/api/channels/email/config'
        );
        setConnectionStatus((prev) => ({
          ...prev,
          email: emailConfig.success && emailConfig.data ? true : false
        }));
      } catch (emailError) {
        // Email not configured yet
        setConnectionStatus((prev) => ({
          ...prev,
          email: false
        }));
      }

      // Check Live Chat configuration
      try {
        const liveChatConfig = await httpClient.get<{ success: boolean; data: any }>(
          '/api/channels/live-chat/config'
        );
        setConnectionStatus((prev) => ({
          ...prev,
          live_chat: liveChatConfig.success && liveChatConfig.data ? true : false
        }));
      } catch (liveChatError) {
        setConnectionStatus((prev) => ({
          ...prev,
          live_chat: false
        }));
      }
    } catch (error) {
      console.error('Failed to check connection status:', error);
    }
  };

  const handleChannelSelect = (channelId: ChannelType) => {
    setSelectedChannel(channelId);
  };

  const handleBack = () => {
    setSelectedChannel(null);
    checkConnectionStatus(); // Refresh status when going back
  };

  const channels = [
    {
      id: 'messenger' as const,
      name: t('atrium.messenger.title', 'Facebook Messenger'),
      description: t(
        'atrium.messenger.description',
        'Connect with customers via Facebook Messenger'
      ),
      icon: MessageCircle,
      color: 'blue',
      available: true
    },
    {
      id: 'email' as const,
      name: t('atrium.email.title', 'Email'),
      description: t('atrium.email.description', 'Receive and respond to customer emails'),
      icon: Mail,
      color: 'green',
      available: true
    },
    {
      id: 'live_chat' as const,
      name: t('atrium.liveChat.title', 'Live Chat'),
      description: t(
        'atrium.liveChat.description',
        'Enable a widget key for partners to embed your live chat'
      ),
      icon: MessagesSquare,
      color: 'orange',
      available: true
    },
    {
      id: 'viber' as const,
      name: t('atrium.viber.title', 'Viber'),
      description: t('atrium.viber.description', 'Connect with customers via Viber'),
      icon: Send,
      color: 'purple',
      available: false
    }
  ];

  if (selectedChannel) {
    return (
      <div>
        {selectedChannel === 'messenger' &&
          (connectionStatus.messenger ? (
            <FacebookPageConnector onBack={handleBack} />
          ) : (
            <div>
              <button
                onClick={handleBack}
                className={`mb-4 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                ← {t('common.back', 'Back')}
              </button>
              <MessengerSetupForm />
            </div>
          ))}
        {selectedChannel === 'email' && (
          <div>
            <button
              onClick={handleBack}
              className={`mb-4 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              ← {t('common.back', 'Back')}
            </button>
            <EmailSettings />
          </div>
        )}
        {selectedChannel === 'live_chat' && (
          <div>
            <button
              onClick={handleBack}
              className={`mb-4 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              ← {t('common.back', 'Back')}
            </button>
            <LiveChatSetupForm />
          </div>
        )}
        {selectedChannel === 'viber' && (
          <div>
            <button
              onClick={handleBack}
              className={`mb-4 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                theme === 'dark'
                  ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              ← {t('common.back', 'Back')}
            </button>
              <div
                className={`p-8 text-center rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                }`}
              >
                <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                  {t('settings.omnichannel.comingSoon', 'Coming soon...')}
                </p>
              </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2
          className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
        >
          {t('atrium.title')}
        </h2>
        <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('atrium.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {channels.map((channel) => {
          const Icon = channel.icon;
          const isConnected = connectionStatus[channel.id];

          return (
            <button
              key={channel.id}
              onClick={() => channel.available && handleChannelSelect(channel.id)}
              disabled={!channel.available}
              className={`
                relative p-6 rounded-lg border-2 text-left transition-all
                ${
                  channel.available
                    ? theme === 'dark'
                      ? 'border-gray-700 hover:border-indigo-500 bg-gray-800 hover:bg-gray-750'
                      : 'border-gray-200 hover:border-indigo-500 bg-white hover:shadow-lg'
                    : theme === 'dark'
                      ? 'border-gray-700 bg-gray-800 opacity-60 cursor-not-allowed'
                      : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                }
              `}
            >
              {!channel.available && (
                <span
                  className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium rounded ${
                    theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {t('common.comingSoon', 'Coming Soon')}
                </span>
              )}

              {channel.available && isConnected && (
                <span
                  className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                    theme === 'dark'
                      ? 'bg-green-900/30 text-green-400'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  <CheckCircle className="w-3 h-3" />
                  {t('common.connected', 'Connected')}
                </span>
              )}

              <div
                className={`
                inline-flex p-3 rounded-lg mb-4
                ${
                  channel.color === 'blue'
                    ? theme === 'dark'
                      ? 'bg-blue-900/30 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                    : channel.color === 'green'
                      ? theme === 'dark'
                        ? 'bg-green-900/30 text-green-400'
                        : 'bg-green-100 text-green-600'
                      : channel.color === 'orange'
                        ? theme === 'dark'
                          ? 'bg-orange-900/30 text-orange-400'
                          : 'bg-orange-100 text-orange-700'
                        : theme === 'dark'
                          ? 'bg-purple-900/30 text-purple-400'
                          : 'bg-purple-100 text-purple-600'
                }
              `}
              >
                <Icon className="w-6 h-6" />
              </div>

              <h3
                className={`text-lg font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
              >
                {channel.name}
              </h3>

              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {channel.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
